import { useEffect } from 'react';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import type { CourseStepDto, UserCourseProgressDto } from '@prometeus/contracts';

export const Route = createFileRoute('/certificacao/cursos/$courseId/')({
  component: CursoIndexRedirect,
});

const CAN_DO_SIMULADO_STATUSES = ['all_steps_done', 'eligible_for_quiz'] as const;

function CursoIndexRedirect() {
  const { courseId } = useParams({ from: '/certificacao/cursos/$courseId/' });
  const navigate = useNavigate();

  const { data: courseData } = useQuery({
    queryKey: ['certification-course', courseId],
    queryFn: async () => {
      const response = await api.get(`/certification/courses/${courseId}`);
      return response.data?.data ?? response.data;
    },
    enabled: !!courseId,
  });

  const { data: progress } = useQuery<UserCourseProgressDto | null>({
    queryKey: ['certification-progress', courseId],
    queryFn: async () => {
      try {
        const response = await api.get(`/certification/courses/${courseId}/progress`);
        return response.data?.data ?? response.data;
      } catch {
        return null;
      }
    },
    enabled: !!courseId,
  });

  const steps: CourseStepDto[] = (courseData as any)?.steps ?? [];
  const firstStepId = steps[0]?.id;
  const targetStepId = progress?.currentStepId ?? firstStepId;
  const progressFetched = progress !== undefined;
  const isCertified = progress?.status === 'certified';
  const canDoSimulado =
    progressFetched &&
    progress != null &&
    progress.status != null &&
    CAN_DO_SIMULADO_STATUSES.includes(progress.status as (typeof CAN_DO_SIMULADO_STATUSES)[number]);
  const showContent = progressFetched && progress != null && (isCertified || canDoSimulado);

  useEffect(() => {
    if (!progressFetched || showContent) return;
    if (!courseData) return;
    if (!firstStepId) {
      navigate({ to: '/certificacao' });
      return;
    }
    if (!targetStepId) return;
    navigate({
      to: '/certificacao/cursos/$courseId/etapa/$stepId',
      params: { courseId: courseId!, stepId: targetStepId },
    });
  }, [progressFetched, showContent, courseId, courseData, firstStepId, targetStepId, navigate]);

  if (courseData && !firstStepId) {
    return (
      <div className="p-6 text-center text-gray-500 dark:text-gray-400">
        Este curso ainda não possui etapas.
      </div>
    );
  }

  if (isCertified) {
    return (
      <div
        className="rounded-[10px] bg-white dark:bg-gray-800 p-6 text-center"
        style={{ fontFamily: "'Poppins', sans-serif", boxShadow: '0 0 15px rgba(0,0,0,0.05)' }}
      >
        <h2 className="text-base font-semibold text-[#06141B] dark:text-white mb-2">Você já foi aprovado neste curso</h2>
        <p className="text-sm text-[#253745] dark:text-gray-400 mb-5">
          Seu certificado já foi emitido. Acesse-o pelo botão abaixo.
        </p>
        {progress?.certificateId && (
          <button
            type="button"
            onClick={() =>
              navigate({
                to: '/certificacao/certificados/$id',
                params: { id: progress.certificateId! },
              })
            }
            className="px-4 py-2 text-sm rounded-[10px] bg-[#06141B] text-white font-medium hover:bg-[#11212D] transition-colors focus:outline-none focus:ring-0"
          >
            Ver certificado
          </button>
        )}
      </div>
    );
  }

  if (canDoSimulado) {
    return (
      <div
        className="rounded-[10px] bg-white dark:bg-gray-800 p-6 text-center"
        style={{ fontFamily: "'Poppins', sans-serif", boxShadow: '0 0 15px rgba(0,0,0,0.05)' }}
      >
        <h2 className="text-base font-semibold text-[#06141B] dark:text-white mb-2">Parabéns!</h2>
        <p className="text-sm text-[#253745] dark:text-gray-400 mb-5">Você concluiu todas as etapas deste curso.</p>
        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          <button
            type="button"
            onClick={() => navigate({ to: '/certificacao/cursos/$courseId/simulado', params: { courseId: courseId! } })}
            className="px-4 py-2 text-sm rounded-[10px] bg-[#06141B] text-white font-medium hover:bg-[#11212D] transition-colors focus:outline-none focus:ring-0"
          >
            Fazer o simulado agora
          </button>
          <button
            type="button"
            onClick={() => navigate({ to: '/certificacao' })}
            className="px-4 py-2 text-sm rounded-[10px] bg-[#9BA8AB]/20 dark:bg-gray-700/50 text-[#06141B] dark:text-gray-300 font-medium hover:bg-[#9BA8AB]/30 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-0"
          >
            Fazer outra hora
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 text-center text-[#4A5C6A] dark:text-gray-400" style={{ fontFamily: 'Poppins, sans-serif' }}>
      Redirecionando para a etapa...
    </div>
  );
}
